type TelegramWebAppOrientation = {
  lockOrientation?: () => void;
  unlockOrientation?: () => void;
};

const getTelegramWebApp = (): TelegramWebAppOrientation | null => {
  return (window as any)?.Telegram?.WebApp ?? null;
};

const isTelegramOrientationSupported = (webApp: any): boolean => {
  if (!webApp) return false;

  if (typeof webApp.isVersionAtLeast === "function") {
    try {
      return webApp.isVersionAtLeast("8.0");
    } catch {
      return false;
    }
  }

  const version = String(webApp.version || "");
  const numeric = Number(version.split(".").slice(0, 2).join("."));
  return Number.isFinite(numeric) ? numeric >= 8.0 : false;
};

export const isLandscapeViewport = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia("(orientation: landscape)").matches;
};

export const requestNativeOrientation = async (
  targetLandscape: boolean
): Promise<boolean> => {
  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const verifyTargetOrientation = async (): Promise<boolean> => {
    for (let i = 0; i < 8; i++) {
      if (isLandscapeViewport() === targetLandscape) {
        return true;
      }
      await wait(75);
    }
    return isLandscapeViewport() === targetLandscape;
  };

  const tgWebApp = getTelegramWebApp();
  if (tgWebApp && isTelegramOrientationSupported(tgWebApp)) {
    try {
      if (targetLandscape && typeof tgWebApp.lockOrientation === "function") {
        tgWebApp.lockOrientation();
        return await verifyTargetOrientation();
      }
      if (!targetLandscape && typeof tgWebApp.unlockOrientation === "function") {
        tgWebApp.unlockOrientation();
        return await verifyTargetOrientation();
      }
    } catch (error) {
      console.warn("[Orientation] Telegram orientation API failed:", error);
    }
  }

  const orientationApi = window.screen?.orientation;
  if (orientationApi && typeof orientationApi.lock === "function") {
    try {
      await orientationApi.lock(targetLandscape ? "landscape" : "portrait");
      return await verifyTargetOrientation();
    } catch (error) {
      console.warn("[Orientation] Screen orientation lock failed:", error);
    }
  }

  return false;
};

export const requestFullscreenLandscape = async (): Promise<void> => {
  if (typeof window === "undefined") return;

  const docEl = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
  };
  const currentDoc = document as Document & {
    webkitFullscreenElement?: Element | null;
  };

  const isFullscreen = Boolean(document.fullscreenElement || currentDoc.webkitFullscreenElement);
  if (!isFullscreen) {
    try {
      if (typeof docEl.requestFullscreen === "function") {
        await docEl.requestFullscreen();
      } else if (typeof docEl.webkitRequestFullscreen === "function") {
        await Promise.resolve(docEl.webkitRequestFullscreen());
      }
    } catch (error) {
      console.warn("[Orientation] Fullscreen request failed:", error);
    }
  }

  await requestNativeOrientation(true);
};
